import { getOwningDesignerPage } from '../utils/effectiveHassSource.js';

// Kept as a preview-layer export for existing callers; page ownership is a
// generic Designer concern and is implemented by the resolver module.
export const getSourceObjectPage = getOwningDesignerPage;

function pageForId(pages, pageId) {
    if (pageId === null || pageId === undefined) return null;
    return pages.find(page => page?.obj === 'page' && Number(page.haspid) === Number(pageId)) ?? null;
}

/**
 * Resolve the page used by the Designer canvas for one template instance.
 * A source object always wins; targetPage/underlayPage are only the fallback
 * for a template selected directly in the page UI.
 */
export function getTemplatePreviewPage(pages, activeTemplate, sourceObject = null) {
    if (activeTemplate?.obj !== 'template_page') return null;
    const sourcePage = getSourceObjectPage(sourceObject);
    if (sourcePage?.obj === 'page') return pageForId(pages, sourcePage.haspid);
    return pageForId(pages, activeTemplate.targetPage ?? activeTemplate.underlayPage);
}

/**
 * The complete transient Designer context for an active template preview.
 * None of these fields are written to the template or its export model.
 */
export function createTemplatePreviewState({
    pages = [],
    activeTemplate = null,
    sourceObject = null,
} = {}) {
    return {
        activeTemplate,
        sourceObject,
        previewPage: getTemplatePreviewPage(pages, activeTemplate, sourceObject),
    };
}

export function resetTemplatePreviewState() {
    return createTemplatePreviewState();
}

function getLayerTemplatePreviewState(object) {
    const layer = object?.getLayer?.();
    return layer?.templatePreviewState;
}

/**
 * Return the live preview context for an object inside the active template.
 * The object does not own or cache any part of this context: every call reads
 * the current layer state, so replacing the source immediately becomes
 * visible to template objects.
 */
export function getTemplatePreviewContextForObject(
    object,
    previewState = getLayerTemplatePreviewState(object),
) {
    const activeTemplate = previewState?.activeTemplate;
    if (!activeTemplate || !previewState?.sourceObject) return null;
    return getOwningDesignerPage(object) === activeTemplate
        ? previewState
        : null;
}

/** Return the current source object for a template object, or null. */
export function getTemplatePreviewSourceObject(object, previewState) {
    return getTemplatePreviewContextForObject(object, previewState)?.sourceObject ?? null;
}

/**
 * Return the regular page shown behind an active template.
 *
 * A source object is transient preview context: when present, its regular
 * page wins over the template's persisted targetPage. This keeps the runtime
 * target and the Designer canvas context separate. The page itself is reused
 * by Konva; it is not copied into the template.
 */
export function getTemplatePreviewContextPage(pages, activePage, previewState = {}) {
    if (activePage?.obj !== 'template_page') return null;
    const { sourceObject, previewPage } = previewState ?? {};
    if (Object.prototype.hasOwnProperty.call(previewState ?? {}, 'previewPage')) {
        return pageForId(pages, previewPage?.haspid) ?? null;
    }
    return getTemplatePreviewPage(pages, activePage, sourceObject);
}

/**
 * Decide which existing page groups the canvas should render. Page zero keeps
 * its established single-page behavior; a template's previewPage is an
 * additional, read-only context group.
 */
export function getPagePreviewVisibility(
    pages,
    activePage,
    { singlePageMode, previewState, sourceObject } = {},
) {
    const contextPage = getTemplatePreviewContextPage(
        pages,
        activePage,
        previewState ?? { sourceObject },
    );
    if (!singlePageMode) return { visiblePages: new Set(pages), contextPage };

    const visiblePages = new Set([activePage, contextPage].filter(Boolean));
    return { visiblePages, contextPage };
}

/** Keep the linked page visible to the renderer but out of the hit graph. */
export function setTemplateContextPage(owner, page) {
    if (owner._templateContextPage === page) {
        if (page) page.listening(false);
        return;
    }
    if (owner._templateContextPage) {
        owner._templateContextPage.listening(owner._templateContextPreviousListening);
    }
    owner._templateContextPage = page ?? null;
    if (owner._templateContextPage) {
        owner._templateContextPreviousListening = owner._templateContextPage.listening();
        owner._templateContextPage.listening(false);
    }
}

/** Outline clicks must not turn a read-only context widget into the selection. */
export function isTemplateContextNode(contextPage, node) {
    if (!contextPage || !node || node === contextPage) return false;
    let page = node;
    while (page && page.obj !== 'page' && page.obj !== 'template_page') {
        page = page.parent ?? page.getParent?.();
    }
    return page === contextPage;
}
